import React, { useMemo, useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { collection, onSnapshot } from 'firebase/firestore';
import { db } from '../../firebase';
import { Heading } from '../../components/heading';
import { Text } from '../../components/text';
import { useUser } from '../../components/UserContext';
import { useClients, useContracts, useData, useAfianzadoras, useConvenios } from '../../contexts/DataContext';
import { usePortalView } from '../../contexts/PortalViewContext';
import { getClientExpiryStatus } from '../../utils/documentExpiry';
import { getEmisorNombre } from '../../utils/calculations';
import {
  aggregateProveedores,
  computeConvenioTotals,
  computeEmittedFianzas,
  computePendingFianzas,
  getFianzaStatus,
} from '../../utils/convenioCompliance';
import {
  DocumentTextIcon,
  DocumentCheckIcon,
  FolderOpenIcon,
  ExclamationCircleIcon,
  ExclamationTriangleIcon,
  CheckCircleIcon,
  ShieldCheckIcon,
  BuildingOffice2Icon,
  ClockIcon,
  ChartBarIcon,
} from '@heroicons/react/24/outline';

const formatMXN = (v) =>
  new Intl.NumberFormat('es-MX', { style: 'currency', currency: 'MXN' }).format(v || 0);

const ClientPortalHome = () => {
  const navigate = useNavigate();
  const { userData } = useUser();
  const { clientsList: allClients, clientsLoading } = useClients();
  const { activeClienteId, activeConvenioId } = usePortalView();
  // The Inicio is scoped to the ACTIVE portal context: a fiado context hides
  // every convenio section and vice versa — the two worlds never stack.
  const clientsList = useMemo(
    () => (activeClienteId
      ? allClients.filter((c) => c.id === activeClienteId)
      : activeConvenioId
      ? []
      : allClients),
    [allClients, activeClienteId, activeConvenioId]
  );
  const { conveniosList, conveniosLoading } = useConvenios();
  const { contractsList } = useContracts();
  const { linksById } = useData();
  const { afianzadorasById } = useAfianzadoras();

  // Fetch per-client afianzadoras for TLDR
  const [clientAfianzadorasMap, setClientAfianzadorasMap] = useState({});
  useEffect(() => {
    if (clientsLoading || clientsList.length === 0) return;
    const unsubs = [];
    clientsList.forEach((client) => {
      const unsub = onSnapshot(
        collection(db, 'clientes', client.id, 'afianzadoras'),
        (snap) => {
          const data = snap.docs.map((d) => ({ id: d.id, clientId: client.id, ...d.data() }));
          setClientAfianzadorasMap((prev) => ({ ...prev, [client.id]: data }));
        }
      );
      unsubs.push(unsub);
    });
    return () => unsubs.forEach((u) => u());
  }, [clientsList, clientsLoading]);

  const stats = useMemo(() => {
    const now = Date.now();
    const thirtyDaysMs = 30 * 24 * 60 * 60 * 1000;
    const clientIds = new Set(clientsList.map((c) => c.id));

    let totalContracts = 0;
    let totalFianzas = 0;
    let totalRequeridas = 0;
    let montoTotal = 0;
    let vigentes = 0;
    let vencidas = 0;
    let fianzasPorVencer = 0;

    contractsList.forEach((c) => {
      if (!clientIds.has(c.clientId)) return;
      totalContracts++;
      totalRequeridas += c.fianzasRequeridas?.value?.length || 0;
      (c.fianzas || []).forEach((f) => {
        totalFianzas++;
        montoTotal += Number(f.montoAfianzado) || 0;
        const st = getFianzaStatus(f, now);
        if (st.estado === 'activa') {
          if (st.code === 'vigente') vigentes++;
          else if (st.code === 'vencida') vencidas++;
          const fin = f.finVigenciaFianza?.toMillis?.() || f.finVigenciaFianza || 0;
          if (fin > now && fin < now + thirtyDaysMs) fianzasPorVencer++;
        }
      });
    });

    let docsExpired = 0;
    let docsWarning = 0;
    clientsList.forEach((c) => {
      const s = getClientExpiryStatus(c.links || [], linksById);
      docsExpired += s.expiredCount;
      docsWarning += s.warningCount;
    });

    // Requisitos sin fianza emitida. Futuras (momento 'despues_termino' —
    // vicios ocultos) are not actionable and never count as "por emitir",
    // same rule as every other surface.
    const clientContracts = contractsList.filter((c) => clientIds.has(c.clientId));
    const porEmitir = computePendingFianzas(clientContracts).filter((fr) => !fr.isFutura).length;

    return { totalContracts, totalFianzas, totalRequeridas, montoTotal, vigentes, vencidas, fianzasPorVencer, docsExpired, docsWarning, porEmitir };
  }, [clientsList, contractsList, linksById]);

  // Convenio stats — scoped to the active context like the cliente side above.
  const hasClientAccess = clientsList.length > 0;
  const accessibleConvenios = useMemo(() => {
    if (!userData?.convenioAccess || activeClienteId) return [];
    const accessIds = new Set(Object.keys(userData.convenioAccess));
    const base = conveniosList.filter((c) => accessIds.has(c.id));
    return activeConvenioId ? base.filter((c) => c.id === activeConvenioId) : base;
  }, [conveniosList, userData?.convenioAccess, activeClienteId, activeConvenioId]);
  const hasConvenioAccess = accessibleConvenios.length > 0;

  // Per-convenio rollup, plus aggregate totals
  const { convenioStatsById } = useMemo(() => {
    const now = Date.now();
    const byId = {};
    accessibleConvenios.forEach((conv) => {
      byId[conv.id] = { totalContratos: 0, totalFianzas: 0, vigentes: 0, vencidas: 0, montoTotal: 0 };
    });

    contractsList.forEach((c) => {
      const bucket = c.convenioId ? byId[c.convenioId] : null;
      if (!bucket) return;
      bucket.totalContratos++;
      (c.fianzas || []).forEach((f) => {
        bucket.totalFianzas++;
        bucket.montoTotal += Number(f.montoAfianzado) || 0;
        const fin = f.finVigenciaFianza?.toMillis?.() || f.finVigenciaFianza || 0;
        const inicio = f.inicioVigenciaFianza?.toMillis?.() || f.inicioVigenciaFianza || 0;
        if (fin && fin < now) bucket.vencidas++;
        else if (inicio && inicio <= now) bucket.vigentes++;
      });
    });

    const agg = { totalContratos: 0, totalFianzas: 0, vigentes: 0, vencidas: 0, montoTotal: 0 };
    Object.values(byId).forEach((s) => {
      agg.totalContratos += s.totalContratos;
      agg.totalFianzas += s.totalFianzas;
      agg.vigentes += s.vigentes;
      agg.vencidas += s.vencidas;
      agg.montoTotal += s.montoTotal;
    });

    return { convenioStats: agg, convenioStatsById: byId };
  }, [accessibleConvenios, contractsList]);

  // Rich beneficiario dashboard data: salud, próximos vencimientos, distribución, top proveedores
  const beneficiarioDashboard = useMemo(() => {
    if (!accessibleConvenios.length) return null;

    const allBeneficiariosById = {};
    const allProyectosById = {};
    const contractIdToConvenioId = {};
    const accessibleContractIds = new Set();

    accessibleConvenios.forEach((conv) => {
      (conv.beneficiarios || []).forEach((b) => { allBeneficiariosById[b.id] = b; });
      (conv.proyectos || []).forEach((p) => { allProyectosById[p.id] = p; });
      const access = userData?.convenioAccess?.[conv.id];
      const convContractIds = new Set(conv.contracts || []);
      if (access?.fullAccess) {
        convContractIds.forEach((id) => {
          accessibleContractIds.add(id);
          contractIdToConvenioId[id] = conv.id;
        });
      } else if (access?.proyectoIds?.length) {
        const proyectoIds = new Set(access.proyectoIds);
        contractsList.forEach((c) => {
          if (convContractIds.has(c.id) && c.proyectoId && proyectoIds.has(c.proyectoId)) {
            accessibleContractIds.add(c.id);
            contractIdToConvenioId[c.id] = conv.id;
          }
        });
      }
    });

    const accessibleContracts = contractsList.filter((c) => accessibleContractIds.has(c.id));
    const proveedoresAgg = aggregateProveedores(accessibleContracts, allBeneficiariosById, allProyectosById);
    const totals = computeConvenioTotals(proveedoresAgg);
    const allFianzas = computeEmittedFianzas(accessibleContracts, allBeneficiariosById, allProyectosById);

    const now = Date.now();
    const day30 = now + 30 * 86400000;
    const day60 = now + 60 * 86400000;
    const day90 = now + 90 * 86400000;

    // Upcoming expirations within 90 days
    const upcomingExpirations = allFianzas
      .map((f) => {
        const fin = f.finVigenciaFianza?.toMillis?.() || f.finVigenciaFianza || 0;
        return { ...f, _finMs: fin };
      })
      .filter((f) => f._finMs > now && f._finMs <= day90)
      .sort((a, b) => a._finMs - b._finMs);

    const expBuckets = {
      d30: upcomingExpirations.filter((f) => f._finMs <= day30).length,
      d60: upcomingExpirations.filter((f) => f._finMs > day30 && f._finMs <= day60).length,
      d90: upcomingExpirations.filter((f) => f._finMs > day60 && f._finMs <= day90).length,
    };

    // Afianzadora distribution (count vigentes by emisor)
    const afianzadoraMap = new Map();
    let montoVigenteTotal = 0;
    allFianzas.forEach((f) => {
      const status = getFianzaStatus(f, now);
      if (status.label !== 'Vigente') return;
      let emisorNombre = getEmisorNombre(f);
      if (!emisorNombre && f.emisor && afianzadorasById[f.emisor]) {
        emisorNombre = afianzadorasById[f.emisor].nombre;
      }
      const nombre = (emisorNombre || 'Sin afianzadora').trim();
      const key = nombre.toLowerCase();
      if (!afianzadoraMap.has(key)) {
        const cat = Object.values(afianzadorasById).find(
          (a) => a.nombre && a.nombre.trim().toLowerCase() === key
        );
        afianzadoraMap.set(key, { nombre, logo: cat?.logo || null, count: 0, monto: 0 });
      }
      const entry = afianzadoraMap.get(key);
      entry.count++;
      const monto = Number(f.montoAfianzado) || 0;
      entry.monto += monto;
      montoVigenteTotal += monto;
    });
    const afianzadoraDistribution = Array.from(afianzadoraMap.values())
      .sort((a, b) => b.monto - a.monto);

    // Top proveedores requiring attention
    const topProveedores = proveedoresAgg
      .filter((p) => p.counts.overdue > 0 || p.counts.dueSoon > 0 || p.counts.pendiente > 0 || p.counts.vencida > 0)
      .slice(0, 5)
      .map((p) => ({
        ...p,
        convenioId: contractIdToConvenioId[p.contracts?.[0]] || null,
      }));

    return {
      totals,
      upcomingExpirations,
      expBuckets,
      afianzadoraDistribution,
      montoVigenteTotal,
      topProveedores,
      contractIdToConvenioId,
    };
  }, [accessibleConvenios, contractsList, userData?.convenioAccess, afianzadorasById]);

  // Afianzadora TLDR
  const afianzadoraTldr = useMemo(() => {
    const now = Date.now();
    const result = [];
    const allCas = Object.values(clientAfianzadorasMap).flat();

    allCas.forEach((ca) => {
      const global = afianzadorasById[ca.afianzadoraId];
      if (!global?.nombre) return;
      const catalogName = global.nombre.trim().toLowerCase();
      const clientIds = new Set(clientsList.map((c) => c.id));

      // Count active fianzas matched to this afianzadora
      // Match by XML emisor name OR by emisor field matching catalog ID
      let fianzaCount = 0;
      let totalAfianzado = 0;
      contractsList.forEach((contrato) => {
        if (!clientIds.has(contrato.clientId)) return;
        (contrato.fianzas || []).forEach((f) => {
          const emisorNombre = getEmisorNombre(f);
          const matchByName = emisorNombre && emisorNombre.trim().toLowerCase().includes(catalogName);
          const matchById = !emisorNombre && f.emisor === ca.afianzadoraId;
          if (!matchByName && !matchById) return;
          const fin = f.finVigenciaFianza || f.xmlFianza?.finVigenciaFianza;
          if (fin) {
            const finTs = typeof fin === 'number' ? fin : new Date(fin).getTime();
            if (finTs < now) return; // skip expired
          }
          fianzaCount++;
          totalAfianzado += Number(f.montoAfianzado) || 0;
        });
      });

      const linea = ca.lineaAfianzamiento || 0;
      const disponible = linea > 0 ? linea - totalAfianzado : null;

      result.push({
        nombre: global.nombre,
        logo: global.logo,
        linea,
        totalAfianzado,
        disponible,
        fianzaCount,
      });
    });

    return result;
  }, [clientAfianzadorasMap, afianzadorasById, contractsList, clientsList]);

  // Fiado navigation lands on the Empresa page tabs. Without an active
  // context (legacy accounts) the old standalone pages still resolve.
  const goEmpresa = (tab, params = '') => {
    if (activeClienteId) {
      navigate(`/app/portal/cliente/${activeClienteId}?tab=${tab}${params}`);
      return;
    }
    const legacy = {
      contratos: '/app/portal/contratos',
      fianzas: '/app/portal/fianzas',
      expediente: '/app/portal/expedientes',
      afianzadoras: '/app/portal/afianzadoras',
    };
    navigate(`${legacy[tab]}${params ? `?${params.replace(/^&/, '')}` : ''}`);
  };

  if (clientsLoading || conveniosLoading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-azul-surelia" />
      </div>
    );
  }

  if ((!clientsList || clientsList.length === 0) && !hasConvenioAccess) {
    return (
      <div className="p-6">
        <Heading level={1} className="text-azul-surelia mb-6">Portal del Cliente</Heading>
        <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-12 text-center">
          <FolderOpenIcon className="w-12 h-12 text-gray-400 mx-auto mb-4" />
          <Heading level={3} className="mb-2">No tienes empresas asignadas</Heading>
          <Text className="text-gray-500">
            Contacta a tu agente de fianzas para que te asigne acceso a tus empresas.
          </Text>
        </div>
      </div>
    );
  }

  const hasAlerts = stats.docsExpired > 0 || stats.docsWarning > 0 || stats.fianzasPorVencer > 0 || stats.vencidas > 0;

  return (
    <div className="container mx-auto px-4 py-6 space-y-6">
      {/* Welcome */}
      <div>
        <Heading level={1} className="text-azul-surelia">
          Bienvenido, {userData?.nombre}
        </Heading>
        <Text className="text-zinc-600 mt-1">
          {hasClientAccess && hasConvenioAccess
            ? 'Resumen de tus empresas y convenios de proveedores.'
            : hasConvenioAccess
            ? 'Resumen de tus convenios de proveedores.'
            : `Aquí tienes un resumen de ${clientsList.length > 1 ? 'tus empresas' : 'tu empresa'}.`}
        </Text>
      </div>

      {/* Client Summary Cards */}
      {hasClientAccess && (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          <button
            onClick={() => goEmpresa('contratos')}
            className="bg-white rounded-xl border border-gray-200 shadow-sm p-5 text-left hover:border-azul-surelia/30 hover:shadow-md transition-all"
          >
            <div className="flex items-center gap-2 mb-2">
              <DocumentTextIcon className="w-5 h-5 text-azul-surelia" />
              <Text className="text-xs text-gray-400 uppercase tracking-wide">Contratos</Text>
            </div>
            <div className="text-3xl font-bold text-gray-900">{stats.totalContracts}</div>
          </button>

          <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-5">
            <Text className="text-xs text-gray-400 uppercase tracking-wide mb-2">Monto Afianzado</Text>
            <div className="text-2xl font-bold text-azul-surelia">{formatMXN(stats.montoTotal)}</div>
          </div>

          <button
            onClick={() => goEmpresa('fianzas')}
            className="bg-white rounded-xl border border-gray-200 shadow-sm p-5 text-left hover:border-azul-surelia/30 hover:shadow-md transition-all"
          >
            <div className="flex items-center gap-2 mb-2">
              <DocumentCheckIcon className="w-5 h-5 text-azul-surelia" />
              <Text className="text-xs text-gray-400 uppercase tracking-wide">Fianzas</Text>
            </div>
            <div className="text-3xl font-bold text-gray-900">{stats.totalFianzas}</div>
            <div className="flex items-center gap-3 mt-1 flex-wrap">
              <Text className="text-xs text-green-600 font-medium">{stats.vigentes} vigente{stats.vigentes !== 1 ? 's' : ''}</Text>
              {stats.vencidas > 0 && (
                <Text className="text-xs text-red-600 font-medium">{stats.vencidas} vencida{stats.vencidas !== 1 ? 's' : ''}</Text>
              )}
              {stats.porEmitir > 0 && (
                <Text className="text-xs text-amber-600 font-medium">{stats.porEmitir} por emitir</Text>
              )}
            </div>
          </button>
        </div>
      )}

      {/* Convenios Section — beneficiario dashboard */}
      {hasConvenioAccess && accessibleConvenios.length > 0 && beneficiarioDashboard && (
        <div className="space-y-4">
          <div className="flex items-center gap-2">
            <BuildingOffice2Icon className="w-5 h-5 text-azul-surelia" />
            <Heading level={3} className="text-azul-surelia">Convenios de Proveedores</Heading>
          </div>

          {/* 1. Salud del Convenio */}
          <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-6">
            <Heading level={4} className="text-azul-surelia mb-4">Salud del Convenio</Heading>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
              <div className="bg-green-50 border border-green-100 rounded-lg p-3">
                <Text className="text-xs text-green-700 uppercase tracking-wide">Monto Afianzado</Text>
                <p className="text-lg font-bold text-green-700">{formatMXN(beneficiarioDashboard.montoVigenteTotal)}</p>
              </div>
              <div className="bg-amber-50 border border-amber-100 rounded-lg p-3">
                <Text className="text-xs text-amber-700 uppercase tracking-wide">Sin Afianzar</Text>
                <p className="text-lg font-bold text-amber-700">{formatMXN(beneficiarioDashboard.totals.montoPendiente)}</p>
              </div>
              <div className="bg-red-50 border border-red-100 rounded-lg p-3">
                <Text className="text-xs text-red-700 uppercase tracking-wide">Monto Vencido</Text>
                <p className="text-lg font-bold text-red-700">{formatMXN(beneficiarioDashboard.totals.montoVencido)}</p>
              </div>
              <div className="bg-gray-50 border border-gray-200 rounded-lg p-3">
                <Text className="text-xs text-gray-500 uppercase tracking-wide">Contratos</Text>
                <p className="text-lg font-bold text-gray-900">{beneficiarioDashboard.totals.totalContratos}</p>
              </div>
            </div>
            <div className="mt-3 flex flex-wrap gap-4 text-sm">
              <span className="flex items-center gap-1.5">
                <span className="w-2 h-2 rounded-full bg-green-500" />
                <span className="text-zinc-700">{beneficiarioDashboard.totals.fianzasVigentes} fianzas vigentes</span>
              </span>
              <span className="flex items-center gap-1.5">
                <span className="w-2 h-2 rounded-full bg-amber-500" />
                <span className="text-zinc-700">{beneficiarioDashboard.totals.fianzasPendientes} sin emitir</span>
              </span>
              <span className="flex items-center gap-1.5">
                <span className="w-2 h-2 rounded-full bg-red-500" />
                <span className="text-zinc-700">{beneficiarioDashboard.totals.fianzasVencidas} vencidas</span>
              </span>
            </div>
          </div>

          {/* 2. Próximos Vencimientos */}
          <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-6">
            <div className="flex items-center gap-2 mb-4">
              <ClockIcon className="w-5 h-5 text-amber-600" />
              <Heading level={4} className="text-azul-surelia">Próximos Vencimientos</Heading>
            </div>
            <div className="grid grid-cols-3 gap-3 mb-4">
              <div className="bg-red-50 border border-red-100 rounded-lg p-3 text-center">
                <Text className="text-xs text-red-600">≤ 30 días</Text>
                <p className="text-2xl font-bold text-red-700">{beneficiarioDashboard.expBuckets.d30}</p>
              </div>
              <div className="bg-amber-50 border border-amber-100 rounded-lg p-3 text-center">
                <Text className="text-xs text-amber-600">31–60 días</Text>
                <p className="text-2xl font-bold text-amber-700">{beneficiarioDashboard.expBuckets.d60}</p>
              </div>
              <div className="bg-yellow-50 border border-yellow-100 rounded-lg p-3 text-center">
                <Text className="text-xs text-yellow-700">61–90 días</Text>
                <p className="text-2xl font-bold text-yellow-700">{beneficiarioDashboard.expBuckets.d90}</p>
              </div>
            </div>
            {beneficiarioDashboard.upcomingExpirations.length === 0 ? (
              <Text className="text-sm text-gray-500">No hay fianzas por vencer en los próximos 90 días.</Text>
            ) : (
              <ul className="divide-y divide-gray-100">
                {beneficiarioDashboard.upcomingExpirations.slice(0, 8).map((f) => {
                  const days = Math.max(0, Math.floor((f._finMs - Date.now()) / 86400000));
                  const toneClass = days <= 30
                    ? 'bg-red-100 text-red-700'
                    : days <= 60
                    ? 'bg-amber-100 text-amber-700'
                    : 'bg-yellow-100 text-yellow-700';
                  const convId = beneficiarioDashboard.contractIdToConvenioId[f.contractId];
                  return (
                    <li
                      key={f.id || `${f.contractId}-${f._finMs}`}
                      onClick={() => convId && navigate(`/app/portal/convenios/${convId}?tab=fianzas`)}
                      className="py-2 flex items-center gap-3 cursor-pointer hover:bg-zinc-50 -mx-2 px-2 rounded"
                    >
                      <span className={`min-w-[64px] text-center px-2 py-1 rounded text-xs font-semibold ${toneClass}`}>
                        {days}d
                      </span>
                      <div className="min-w-0 flex-1">
                        <Text className="text-sm font-medium text-zinc-800 truncate">
                          {f.contractNumero || f.contractTitle}
                        </Text>
                        <Text className="text-xs text-zinc-500 truncate">
                          {f.tipoFianza || f.tipo || 'Fianza'} · {f.proyectoNombre || 'Sin proyecto'}
                        </Text>
                      </div>
                      <span className="text-sm font-semibold text-azul-surelia whitespace-nowrap">
                        {formatMXN(f.montoAfianzado)}
                      </span>
                    </li>
                  );
                })}
              </ul>
            )}
          </div>

          {/* 3. Distribución por Afianzadora */}
          <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-6">
            <div className="flex items-center gap-2 mb-4">
              <ChartBarIcon className="w-5 h-5 text-azul-surelia" />
              <Heading level={4} className="text-azul-surelia">Distribución por Afianzadora</Heading>
            </div>
            {beneficiarioDashboard.afianzadoraDistribution.length === 0 ? (
              <Text className="text-sm text-gray-500">No hay fianzas vigentes registradas.</Text>
            ) : (
              <ul className="space-y-3">
                {beneficiarioDashboard.afianzadoraDistribution.map((af) => {
                  const pct = beneficiarioDashboard.montoVigenteTotal > 0
                    ? (af.monto / beneficiarioDashboard.montoVigenteTotal) * 100
                    : 0;
                  return (
                    <li key={af.nombre}>
                      <div className="flex items-center gap-3 mb-1">
                        {af.logo ? (
                          <img src={af.logo} alt={af.nombre} className="w-8 h-8 rounded-lg object-contain bg-white border border-zinc-100 p-0.5 flex-shrink-0" />
                        ) : (
                          <div className="w-8 h-8 rounded-lg bg-azul-surelia/10 flex items-center justify-center text-azul-surelia font-bold text-xs flex-shrink-0">
                            {af.nombre.charAt(0)}
                          </div>
                        )}
                        <div className="min-w-0 flex-1">
                          <Text className="text-sm font-medium text-zinc-800 truncate">{af.nombre}</Text>
                          <Text className="text-xs text-zinc-500">
                            {af.count} fianza{af.count !== 1 ? 's' : ''} · {formatMXN(af.monto)} · {pct.toFixed(1)}%
                          </Text>
                        </div>
                      </div>
                      <div className="w-full bg-gray-100 rounded-full h-2 overflow-hidden">
                        <div
                          className="h-full bg-azul-surelia rounded-full transition-all"
                          style={{ width: `${pct}%` }}
                        />
                      </div>
                    </li>
                  );
                })}
              </ul>
            )}
          </div>

          {/* 4. Top Proveedores con Atención Requerida */}
          <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-6">
            <div className="flex items-center gap-2 mb-4">
              <ExclamationTriangleIcon className="w-5 h-5 text-amber-600" />
              <Heading level={4} className="text-azul-surelia">Proveedores con Atención Requerida</Heading>
            </div>
            {beneficiarioDashboard.topProveedores.length === 0 ? (
              <div className="flex items-center gap-2 text-green-700">
                <CheckCircleIcon className="w-5 h-5" />
                <Text className="text-sm">Todos los proveedores están al día.</Text>
              </div>
            ) : (
              <ul className="divide-y divide-gray-100">
                {beneficiarioDashboard.topProveedores.map((p) => {
                  const exposureMonto = p.montos.pendiente + p.montos.overdue;
                  return (
                    <li
                      key={p.key}
                      onClick={() => p.convenioId && navigate(`/app/portal/convenios/${p.convenioId}?tab=proveedores`)}
                      className="py-3 flex items-center gap-3 cursor-pointer hover:bg-zinc-50 -mx-2 px-2 rounded"
                    >
                      <div className="w-9 h-9 rounded-lg bg-azul-surelia/10 flex items-center justify-center text-azul-surelia font-bold text-sm flex-shrink-0">
                        {(p.label || '?').charAt(0)}
                      </div>
                      <div className="min-w-0 flex-1">
                        <Text className="text-sm font-medium text-zinc-800 truncate">{p.label}</Text>
                        <div className="flex items-center gap-1.5 flex-wrap mt-0.5">
                          {p.counts.overdue > 0 && (
                            <span className="px-1.5 py-0.5 rounded text-[10px] font-semibold bg-red-100 text-red-700">
                              {p.counts.overdue} vencidas
                            </span>
                          )}
                          {p.counts.dueSoon > 0 && (
                            <span className="px-1.5 py-0.5 rounded text-[10px] font-semibold bg-amber-100 text-amber-700">
                              {p.counts.dueSoon} por vencer
                            </span>
                          )}
                          {p.counts.pendiente > 0 && (
                            <span className="px-1.5 py-0.5 rounded text-[10px] font-semibold bg-zinc-100 text-zinc-700">
                              {p.counts.pendiente} sin emitir
                            </span>
                          )}
                          {p.counts.vencida > 0 && (
                            <span className="px-1.5 py-0.5 rounded text-[10px] font-semibold bg-red-100 text-red-700">
                              {p.counts.vencida} fianza{p.counts.vencida !== 1 ? 's' : ''} vencida{p.counts.vencida !== 1 ? 's' : ''}
                            </span>
                          )}
                        </div>
                      </div>
                      <div className="text-right whitespace-nowrap">
                        <Text className="text-sm font-semibold text-azul-surelia">{formatMXN(exposureMonto)}</Text>
                        <Text className="text-[10px] text-zinc-500">exposición</Text>
                      </div>
                    </li>
                  );
                })}
              </ul>
            )}
          </div>

          {/* Mis Convenios — cards */}
          <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-6">
            <Heading level={4} className="text-azul-surelia mb-4">Mis Convenios</Heading>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              {accessibleConvenios.map((conv) => {
                const perConv = convenioStatsById[conv.id] || { vencidas: 0, totalContratos: 0 };
                return (
                  <button
                    key={conv.id}
                    onClick={() => navigate(`/app/portal/convenios/${conv.id}`)}
                    className="flex items-center gap-3 p-4 bg-zinc-50 rounded-lg hover:bg-azul-surelia/5 transition-colors text-left border border-gray-100"
                  >
                    <div className="w-10 h-10 rounded-lg bg-azul-surelia/10 flex items-center justify-center text-azul-surelia font-bold text-sm flex-shrink-0">
                      {(conv.nombre || '?').charAt(0)}
                    </div>
                    <div className="min-w-0 flex-1">
                      <Text className="text-sm font-medium text-zinc-800 truncate">{conv.nombre}</Text>
                      <div className="flex items-center gap-2 text-xs text-zinc-500">
                        <span>{(conv.beneficiarios || []).length} beneficiario{(conv.beneficiarios || []).length !== 1 ? 's' : ''}</span>
                        <span>·</span>
                        <span>{perConv.totalContratos} contrato{perConv.totalContratos !== 1 ? 's' : ''}</span>
                        {perConv.vencidas > 0 && (
                          <>
                            <span>·</span>
                            <span className="text-red-600 font-medium">{perConv.vencidas} vencida{perConv.vencidas !== 1 ? 's' : ''}</span>
                          </>
                        )}
                      </div>
                    </div>
                  </button>
                );
              })}
            </div>
          </div>
        </div>
      )}

      {/* Afianzadoras TLDR — only for client access */}
      {hasClientAccess && afianzadoraTldr.length > 0 && (
        <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-6">
          <div className="flex items-center gap-2 mb-4">
            <ShieldCheckIcon className="w-5 h-5 text-azul-surelia" />
            <Heading level={3} className="text-azul-surelia">Afianzadoras</Heading>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
            {afianzadoraTldr.map((af) => (
              <button
                key={af.nombre}
                onClick={() => goEmpresa('afianzadoras')}
                className="flex items-center gap-3 p-3 bg-zinc-50 rounded-lg hover:bg-azul-surelia/5 transition-colors text-left"
              >
                {af.logo ? (
                  <img src={af.logo} alt={af.nombre} className="w-8 h-8 rounded-lg object-contain bg-white border border-zinc-100 p-0.5 flex-shrink-0" />
                ) : (
                  <div className="w-8 h-8 rounded-lg bg-azul-surelia/10 flex items-center justify-center text-azul-surelia font-bold text-xs flex-shrink-0">
                    {af.nombre.charAt(0)}
                  </div>
                )}
                <div className="min-w-0 flex-1">
                  <Text className="text-sm font-medium text-zinc-800 truncate">{af.nombre}</Text>
                  <div className="flex items-center gap-2 text-xs text-zinc-500">
                    <span>{af.fianzaCount} fianza{af.fianzaCount !== 1 ? 's' : ''}</span>
                    {af.linea > 0 && (
                      <>
                        <span>·</span>
                        <span className={af.disponible < 0 ? 'text-red-600 font-medium' : 'text-emerald-600 font-medium'}>
                          {formatMXN(af.disponible)} disp.
                        </span>
                      </>
                    )}
                  </div>
                </div>
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Alerts or All Clear — only for client access */}
      {hasClientAccess && hasAlerts ? (
        <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-6">
          <Heading level={3} className="text-azul-surelia mb-4">Requiere tu atención</Heading>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            {stats.docsExpired > 0 && (
              <button
                onClick={() => goEmpresa('expediente')}
                className="flex items-center gap-3 text-red-600 bg-red-50 rounded-lg px-4 py-3 text-left hover:bg-red-100 transition-colors"
              >
                <ExclamationCircleIcon className="w-5 h-5 flex-shrink-0" />
                <div>
                  <Text className="text-sm font-semibold">{stats.docsExpired} documento{stats.docsExpired !== 1 ? 's' : ''} vencido{stats.docsExpired !== 1 ? 's' : ''}</Text>
                  <Text className="text-xs text-red-500">Requiere actualización</Text>
                </div>
              </button>
            )}
            {stats.docsWarning > 0 && (
              <button
                onClick={() => goEmpresa('expediente')}
                className="flex items-center gap-3 text-amber-600 bg-amber-50 rounded-lg px-4 py-3 text-left hover:bg-amber-100 transition-colors"
              >
                <ExclamationCircleIcon className="w-5 h-5 flex-shrink-0" />
                <div>
                  <Text className="text-sm font-semibold">{stats.docsWarning} documento{stats.docsWarning !== 1 ? 's' : ''} por vencer</Text>
                  <Text className="text-xs text-amber-500">Próximos a vencerse</Text>
                </div>
              </button>
            )}
            {stats.fianzasPorVencer > 0 && (
              <button
                onClick={() => goEmpresa('fianzas', '&vigencia=v30')}
                className="flex items-center gap-3 text-amber-600 bg-amber-50 rounded-lg px-4 py-3 text-left hover:bg-amber-100 transition-colors"
              >
                <ExclamationCircleIcon className="w-5 h-5 flex-shrink-0" />
                <div>
                  <Text className="text-sm font-semibold">{stats.fianzasPorVencer} fianza{stats.fianzasPorVencer !== 1 ? 's' : ''} por vencer</Text>
                  <Text className="text-xs text-amber-500">En los próximos 30 días</Text>
                </div>
              </button>
            )}
            {stats.vencidas > 0 && (
              <button
                onClick={() => goEmpresa('fianzas', '&estatus=vencida')}
                className="flex items-center gap-3 text-red-600 bg-red-50 rounded-lg px-4 py-3 text-left hover:bg-red-100 transition-colors"
              >
                <ExclamationCircleIcon className="w-5 h-5 flex-shrink-0" />
                <div>
                  <Text className="text-sm font-semibold">{stats.vencidas} fianza{stats.vencidas !== 1 ? 's' : ''} vencida{stats.vencidas !== 1 ? 's' : ''}</Text>
                  <Text className="text-xs text-red-500">Ya pasó la fecha de vigencia</Text>
                </div>
              </button>
            )}
          </div>
        </div>
      ) : hasClientAccess ? (
        <div className="flex items-center gap-3 text-green-600 bg-green-50 rounded-xl border border-green-200 p-5">
          <CheckCircleIcon className="w-6 h-6" />
          <div>
            <Text className="font-semibold text-green-700">Todo en orden</Text>
            <Text className="text-sm text-green-600">No hay alertas pendientes. Tus documentos y fianzas están al día.</Text>
          </div>
        </div>
      ) : null}

      {/* Quick links — only for client access */}
      {hasClientAccess && <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <button
          onClick={() => goEmpresa('expediente')}
          className="bg-white rounded-xl border border-gray-200 shadow-sm p-5 text-left hover:border-azul-surelia/30 hover:shadow-md transition-all group"
        >
          <FolderOpenIcon className="w-8 h-8 text-azul-surelia/60 group-hover:text-azul-surelia transition-colors mb-3" />
          <Heading level={3} className="text-azul-surelia mb-1">Expediente</Heading>
          <Text className="text-sm text-gray-500">Ver el estado de tus documentos</Text>
        </button>
        <button
          onClick={() => goEmpresa('contratos')}
          className="bg-white rounded-xl border border-gray-200 shadow-sm p-5 text-left hover:border-azul-surelia/30 hover:shadow-md transition-all group"
        >
          <DocumentTextIcon className="w-8 h-8 text-azul-surelia/60 group-hover:text-azul-surelia transition-colors mb-3" />
          <Heading level={3} className="text-azul-surelia mb-1">Contratos</Heading>
          <Text className="text-sm text-gray-500">Consulta todos tus contratos y su estado</Text>
        </button>
        <button
          onClick={() => goEmpresa('fianzas')}
          className="bg-white rounded-xl border border-gray-200 shadow-sm p-5 text-left hover:border-azul-surelia/30 hover:shadow-md transition-all group"
        >
          <DocumentCheckIcon className="w-8 h-8 text-azul-surelia/60 group-hover:text-azul-surelia transition-colors mb-3" />
          <Heading level={3} className="text-azul-surelia mb-1">Fianzas</Heading>
          <Text className="text-sm text-gray-500">Ver y descargar tus fianzas emitidas</Text>
        </button>
      </div>}
    </div>
  );
};

export default ClientPortalHome;
